import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";

const CurrencyDropdown = ({ currency, onCurrencyChange, variant = "light" }) => {
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isDark = variant === "dark";

  useEffect(() => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const defaultFirst = timeZone.includes("India") ? "INR" : "USD";
    setOptions([defaultFirst, defaultFirst === "INR" ? "USD" : "INR"]);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (cur) => {
    onCurrencyChange(cur);
    localStorage.setItem("currency", cur);
    setOpen(false);
  };

  const buttonClass = isDark
    ? "flex items-center gap-2 px-3 py-2 rounded-[4px] border border-white/20 bg-black/50 text-white text-sm hover:border-white/40 hover:bg-white/10 transition-all duration-200 min-w-[80px] justify-between"
    : "flex items-center gap-2 px-3 py-2 rounded-[4px] border border-gray-300 bg-white text-black text-sm hover:border-gray-400 transition-all duration-200 min-w-[80px] justify-between";

  const panelClass = isDark
    ? "absolute z-50 bottom-full mb-1 left-0 w-full rounded-[4px] border border-white/20 bg-[#111111] overflow-hidden shadow-lg"
    : "absolute z-50 bottom-full mb-1 left-0 w-full rounded-[4px] border border-gray-200 bg-white overflow-hidden shadow-lg";

  const optionBase = "w-full text-left px-3 py-2 text-sm transition-colors duration-150 flex items-center gap-2";
  const optionDark = `${optionBase} text-white/70 hover:bg-white/10 hover:text-white`;
  const optionLight = `${optionBase} text-gray-700 hover:bg-gray-50`;
  const optionClass = isDark ? optionDark : optionLight;

  return (
    <div ref={ref} className="relative currency-dropdown">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={buttonClass}
        aria-label="Select currency"
        aria-expanded={open}
      >
        <span>{currency}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        >
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className={panelClass}>
          {options.map((cur) => (
            <button
              key={cur}
              type="button"
              onClick={() => handleSelect(cur)}
              className={optionClass}
            >
              {cur === currency && (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              {cur !== currency && <span className="w-3" />}
              {cur}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

CurrencyDropdown.propTypes = {
  currency: PropTypes.string.isRequired,
  onCurrencyChange: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(["light", "dark"]),
};

export default CurrencyDropdown;
